import React, { useState, useEffect } from "react";
import {
  getAllGames,
  getRecommendedPlaytesters,
  searchPlaytesters,
  getAllPlaytesters,
  manualAssignPlaytesters,
  getAllAssignments,
} from "../../services/adminAssignmentService";
import {
  getPlaytestTypeLabel,
  isAdminAssignablePlaytest,
  resolvePlaytestTypeId,
  getGamePlaytime,
  FREE_PRIVATE_MAX_PLAYTESTERS,
} from "../../utils/playtestTypes";
import "./ManualAssignment.scss";

/** Convert an API deadline into a value for <input type="datetime-local"> */
function toDatetimeLocalValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function getApprovedSessionCount(playtester) {
  return Number(
    playtester?.approvedSessions ??
    playtester?.stats?.approvedSessions ??
    0
  );
}

function getJoinedAt(playtester) {
  const value = playtester?.memberSince || playtester?.createdAt || playtester?.joinedAt;
  const time = value ? new Date(value).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
}

function formatJoinedDate(playtester) {
  const time = getJoinedAt(playtester);
  if (!time) return "Join date unknown";
  return `Joined ${new Date(time).toLocaleDateString()}`;
}

function sortPlaytesters(playtesters, sortMode) {
  return [...(playtesters || [])].sort((a, b) => {
    if (sortMode === "newest") return getJoinedAt(b) - getJoinedAt(a);
    if (sortMode === "oldest") return getJoinedAt(a) - getJoinedAt(b);
    const byApproved = getApprovedSessionCount(b) - getApprovedSessionCount(a);
    if (byApproved !== 0) return byApproved;
    return Number(b.matchScore || 0) - Number(a.matchScore || 0);
  });
}

const PLAYTESTER_SORT_OPTIONS = [
  { id: "approved", label: "Most approved sessions" },
  { id: "newest", label: "Newest members" },
  { id: "oldest", label: "Oldest members" },
];

const COHORT_SECTIONS = [
  {
    id: "relevance",
    title: "Relevance",
    blurb: "Genre fit and prior play on similar games.",
  },
  {
    id: "reliable",
    title: "Most reliable",
    blurb: "High approval rate and both gameplay and webcam evidence.",
  },
  {
    id: "diversified",
    title: "Diversified cohort",
    blurb: "A mixed set of country, gender, and age.",
  },
  {
    id: "newPlaytesters",
    title: "New playtesters",
    blurb: "Random signups with no sessions yet. The draw stays the same for this game today.",
  },
];

const EMPTY_COHORTS = {
  relevance: [],
  reliable: [],
  diversified: [],
  newPlaytesters: [],
};

/**
 * Manual Assignment Component
 * 6-Step Flow for Admin to Manually Assign Playtesters to Games
 */
const ManualAssignment = ({ onAssignmentComplete }) => {
  // Step tracking
  const [currentStep, setCurrentStep] = useState(1);
  
  // Step 1: Select Game
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [assignmentCountsByGameId, setAssignmentCountsByGameId] = useState({});
  
  // Step 2: View Recommended Playtesters
  const [recommendedPlaytesters, setRecommendedPlaytesters] = useState([]);
  const [cohorts, setCohorts] = useState(EMPTY_COHORTS);
  const [showAllPlaytesters, setShowAllPlaytesters] = useState(false);
  const [allPlaytesters, setAllPlaytesters] = useState([]);
  const [playtesterSort, setPlaytesterSort] = useState("approved");
  
  // Step 3: Select Playtesters
  const [selectedPlaytesters, setSelectedPlaytesters] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Step 4: Configure Assignment
  const [deadline, setDeadline] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [shouldNotify, setShouldNotify] = useState(true);
  
  // Step 5 & 6: Submit & Confirmation
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assignmentResult, setAssignmentResult] = useState(null);
  
  // Common state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load games on mount
  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllGames();
      const fetchedGames = data.games || [];
      setGames(fetchedGames);

      // Build per-game assignment counts to avoid relying on potentially aggregated stats.
      const countEntries = await Promise.all(
        fetchedGames.map(async (game) => {
          const gameId = game._id || game.gameId;
          if (!gameId) return [null, 0];

          try {
            const assignmentData = await getAllAssignments({ gameId });
            const gameAssignments = assignmentData?.assignments || [];
            return [String(gameId), gameAssignments.length];
          } catch (countErr) {
            console.warn(`Failed to load assignment count for game ${gameId}:`, countErr);
            return [String(gameId), null];
          }
        })
      );

      const countMap = countEntries.reduce((acc, [gameId, count]) => {
        if (gameId) {
          acc[gameId] = count;
        }
        return acc;
      }, {});

      setAssignmentCountsByGameId(countMap);
    } catch (err) {
      console.error("Error loading games:", err);
      setError("Failed to load games. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Game Selection
  const handleGameSelect = async (game) => {
    if (!isAdminAssignablePlaytest(game)) {
      setError("Public playtests are opt-in. Playtesters join themselves from the Public Playtest tab.");
      return;
    }
    setSelectedGame(game);
    setDeadline(toDatetimeLocalValue(game.deadline));
    setCurrentStep(2);
    
    // Fetch recommended playtesters
    try {
      setLoading(true);
      setError(null);
      const gameId = game.gameId || game._id;
      const data = await getRecommendedPlaytesters(gameId);
      setRecommendedPlaytesters(data.recommendedPlaytesters || data.playtesters || []);
      setCohorts({ ...EMPTY_COHORTS, ...(data.cohorts || {}) });
    } catch (err) {
      console.error("Error loading recommended playtesters:", err);
      setError("Failed to load recommended playtesters.");
    } finally {
      setLoading(false);
    }
  };

  const browseParamsForSort = (mode) => {
    const params = { limit: 500 };
    if (mode === "newest") {
      params.sortBy = "createdAt";
      params.sortOrder = "desc";
    } else if (mode === "oldest") {
      params.sortBy = "createdAt";
      params.sortOrder = "asc";
    }
    return params;
  };

  const fetchAllPlaytesters = async (mode = playtesterSort) => {
    const data = await getAllPlaytesters(browseParamsForSort(mode));
    setAllPlaytesters(data.playtesters || []);
  };

  // Step 2: Load all playtesters if user wants to browse
  const handleShowAllPlaytesters = async () => {
    if (allPlaytesters.length === 0) {
      try {
        setLoading(true);
        await fetchAllPlaytesters();
      } catch (err) {
        console.error("Error loading all playtesters:", err);
        setError("Failed to load all playtesters.");
      } finally {
        setLoading(false);
      }
    }
    setShowAllPlaytesters(true);
  };

  const handlePlaytesterSortChange = async (mode) => {
    setPlaytesterSort(mode);
    if (!showAllPlaytesters) return;
    try {
      setLoading(true);
      await fetchAllPlaytesters(mode);
    } catch (err) {
      console.error("Error reloading playtesters:", err);
      setError("Failed to reload playtesters.");
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Search playtesters
  const handleSearch = async (query) => {
    const normalizedQuery = query.trimStart();
    const trimmedQuery = normalizedQuery.trim();
    setSearchQuery(normalizedQuery);

    // Empty input should fall back to browse-all data instead of blank results.
    if (trimmedQuery.length === 0) {
      setSearchResults([]);
      setIsSearching(false);
      await handleShowAllPlaytesters();
      return;
    }

    if (trimmedQuery.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    try {
      setIsSearching(true);
      const data = await searchPlaytesters(trimmedQuery);
      setSearchResults(data.results || []);
    } catch (err) {
      console.error("Error searching playtesters:", err);
      setError("Search failed. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  // Step 3: Toggle playtester selection
  const togglePlaytesterSelection = (playtester) => {
    setSelectedPlaytesters((prev) => {
      const isSelected = prev.some((p) => p.playtesterId === playtester.playtesterId);
      if (isSelected) {
        return prev.filter((p) => p.playtesterId !== playtester.playtesterId);
      }
      if (resolvePlaytestTypeId(selectedGame) === "free_private") {
        const alreadyAssigned =
          assignmentCountsByGameId[String(selectedGame?._id || selectedGame?.gameId)] || 0;
        if (alreadyAssigned + prev.length + 1 > FREE_PRIVATE_MAX_PLAYTESTERS) {
          alert(`Free private playtests are capped at ${FREE_PRIVATE_MAX_PLAYTESTERS} playtesters.`);
          return prev;
        }
      }
      return [...prev, playtester];
    });
  };

  const isPlaytesterSelected = (playtesterId) => {
    return selectedPlaytesters.some((p) => p.playtesterId === playtesterId);
  };

  const selectCohort = (cohortId) => {
    const members = cohorts[cohortId] || [];
    setSelectedPlaytesters((prev) => {
      const next = [...prev];
      const alreadyAssigned =
        assignmentCountsByGameId[String(selectedGame?._id || selectedGame?.gameId)] || 0;
      const isFreePrivate = resolvePlaytestTypeId(selectedGame) === "free_private";
      members.forEach((playtester) => {
        if (next.some((p) => p.playtesterId === playtester.playtesterId)) return;
        if (isFreePrivate && alreadyAssigned + next.length + 1 > FREE_PRIVATE_MAX_PLAYTESTERS) return;
        next.push(playtester);
      });
      return next;
    });
    setCurrentStep(3);
    setShowAllPlaytesters(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  // Step 3 to 4: Proceed to configuration
  const proceedToConfiguration = () => {
    if (selectedPlaytesters.length === 0) {
      alert("Please select at least one playtester.");
      return;
    }
    setCurrentStep(4);
  };

  // Step 5: Submit assignment
  const handleSubmitAssignment = async () => {
    if (!deadline) {
      alert("Please set a deadline for the assignment.");
      return;
    }
    const parsedDeadline = new Date(deadline);
    if (Number.isNaN(parsedDeadline.getTime())) {
      alert("Please provide a valid deadline.");
      return;
    }

    const assignmentData = {
      gameId: selectedGame._id || selectedGame.gameId,
      playtesterIds: selectedPlaytesters.map((p) => p.playtesterId),
      deadline: parsedDeadline.toISOString(),
      customMessage: customMessage.trim() || undefined,
      notifyPlaytesters: shouldNotify,
    };

    try {
      setIsSubmitting(true);
      setCurrentStep(5);
      const result = await manualAssignPlaytesters(assignmentData);
      setAssignmentResult(result);
      setCurrentStep(6);
      if (onAssignmentComplete) {
        onAssignmentComplete();
      }
    } catch (err) {
      console.error("Error submitting assignment:", err);
      setError(`Failed to assign playtesters: ${err.response?.data?.message || err.message}`);
      setCurrentStep(4); // Go back to configuration
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset flow
  const handleReset = () => {
    setCurrentStep(1);
    setSelectedGame(null);
    setRecommendedPlaytesters([]);
    setAllPlaytesters([]);
    setSelectedPlaytesters([]);
    setSearchQuery("");
    setSearchResults([]);
    setDeadline("");
    setCustomMessage("");
    setShouldNotify(true);
    setAssignmentResult(null);
    setError(null);
    setShowAllPlaytesters(false);
    setPlaytesterSort("approved");
  };

  // Render progress indicator
  const renderProgressIndicator = () => {
    const steps = [
      { num: 1, label: "Select Game" },
      { num: 2, label: "View Matches" },
      { num: 3, label: "Select Playtesters" },
      { num: 4, label: "Configure" },
      { num: 5, label: "Submit" },
      { num: 6, label: "Confirm" },
    ];

    return (
      <div className="progress-indicator">
        {steps.map((step, idx) => (
          <React.Fragment key={step.num}>
            <div className={`progress-step ${currentStep >= step.num ? "active" : ""} ${currentStep === step.num ? "current" : ""}`}>
              <div className="step-number">{step.num}</div>
              <div className="step-label">{step.label}</div>
            </div>
            {idx < steps.length - 1 && (
              <div className={`progress-line ${currentStep > step.num ? "active" : ""}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // Render Step 1: Select Game
  const renderStep1 = () => {
    const rankedGames = [...games].sort((a, b) => {
      const aAssignable = isAdminAssignablePlaytest(a) ? 0 : 1;
      const bAssignable = isAdminAssignablePlaytest(b) ? 0 : 1;
      if (aAssignable !== bAssignable) return aAssignable - bAssignable;
      const aPending = a.status === "pending" ? 0 : 1;
      const bPending = b.status === "pending" ? 0 : 1;
      if (aPending !== bPending) return aPending - bPending;
      const aAssigned = assignmentCountsByGameId[String(a._id || a.gameId)] ?? a.stats?.totalAssignments ?? 0;
      const bAssigned = assignmentCountsByGameId[String(b._id || b.gameId)] ?? b.stats?.totalAssignments ?? 0;
      const aNeed = Math.max(0, (a.numPlaytesters || 0) - aAssigned);
      const bNeed = Math.max(0, (b.numPlaytesters || 0) - bAssigned);
      return bNeed - aNeed;
    });

    return (
    <div className="assignment-step step-1">
      <h2>Step 1: Select a Game</h2>
      <p className="step-description">Choose a private playtest to assign playtesters. Public playtests stay opt-in.</p>

      {loading ? (
        <div className="loading-state">Loading games...</div>
      ) : games.length === 0 ? (
        <div className="empty-state">
          <p>No games found. Developers need to create games first.</p>
        </div>
      ) : (
        <div className="games-grid">
          {rankedGames.map((game) => {
            const assignable = isAdminAssignablePlaytest(game);
            const typeId = resolvePlaytestTypeId(game);
            const assignedCount =
              assignmentCountsByGameId[String(game._id || game.gameId)] ??
              game.stats?.totalAssignments ??
              0;
            return (
            <div
              key={game.gameId || game._id}
              className={`game-card ${assignable ? "" : "game-card--disabled"}`}
              onClick={() => assignable && handleGameSelect(game)}
            >
              <h3>{game.name}</h3>
              <div className="game-meta">
                <span className="game-genre">{game.genre || "No Genre"}</span>
                {getPlaytestTypeLabel(game) && (
                  <span className={`playtest-type-badge playtest-type-badge--${typeId}`}>
                    {getPlaytestTypeLabel(game)}
                  </span>
                )}
                <span className="game-status">{game.status}</span>
              </div>
              <p className="game-description">{game.description || "No description provided"}</p>
              <div className="game-stats">
                <span>
                  {assignedCount}/{game.numPlaytesters || 0} Assigned
                </span>
                <span>{getGamePlaytime(game).summaryLabel}</span>
                <span>Deadline: {game.deadline ? new Date(game.deadline).toLocaleDateString() : "None"}</span>
              </div>
              {assignable ? (
                <button type="button" className="select-button">Select Game</button>
              ) : (
                <button type="button" className="select-button" disabled>
                  Opt-in public playtest
                </button>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
    );
  };

  const renderPlaytesterSort = () => (
    <div className="playtester-sort-row">
      <label htmlFor="playtesterSort">Sort by</label>
      <select
        id="playtesterSort"
        value={playtesterSort}
        onChange={(e) => handlePlaytesterSortChange(e.target.value)}
      >
        {PLAYTESTER_SORT_OPTIONS.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );

  // Render Step 2: View Recommended Playtesters
  const renderCohortCard = (playtester) => {
    const reasons = playtester.reasons || playtester.matchReasons || [];
    const profileBits = [playtester.country, playtester.ageBand]
      .filter((part) => part && part !== "unknown")
      .join(" · ");
    return (
      <div key={`${playtester.cohort}-${playtester.playtesterId}`} className="playtester-card">
        <div className="playtester-header">
          <h4>{playtester.name || playtester.username || "Unknown"}</h4>
          {playtester.cohort !== "newPlaytesters" && playtester.cohortScore ? (
            <span className="rank-badge">{playtester.cohortScore}</span>
          ) : null}
        </div>
        <div className="playtester-stats">
          <span>{getApprovedSessionCount(playtester)} approved sessions</span>
          {profileBits ? <span>{profileBits}</span> : <span>{formatJoinedDate(playtester)}</span>}
          <span>{playtester.isAvailable ? "Available" : "Busy"}</span>
        </div>
        {reasons.length > 0 && (
          <ul className="cohort-reasons">
            {reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const renderStep2 = () => {
    return (
    <div className="assignment-step step-2">
      <h2>Step 2: Suggested playtesters</h2>
      <p className="step-description">
        Four ways to pick people for <strong>{selectedGame?.name}</strong>. Selecting a group checks those people on the next step.
      </p>

      {loading ? (
        <div className="loading-state">Loading recommendations...</div>
      ) : (
        <>
          {COHORT_SECTIONS.map((section) => {
            const members = cohorts[section.id] || [];
            return (
              <section key={section.id} className="cohort-section">
                <div className="cohort-section-header">
                  <div>
                    <h3>{section.title}</h3>
                    <p>{section.blurb}</p>
                  </div>
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={members.length === 0}
                    onClick={() => selectCohort(section.id)}
                  >
                    Select this group
                  </button>
                </div>
                {members.length > 0 ? (
                  <div className="playtesters-list">
                    {members.map((playtester) => renderCohortCard(playtester))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <p>No playtesters in this group.</p>
                  </div>
                )}
              </section>
            );
          })}

          <div className="step-actions">
            <button className="btn-secondary" onClick={handleShowAllPlaytesters}>
              Browse all playtesters
            </button>
            <button className="btn-primary" onClick={() => setCurrentStep(3)}>
              Continue to selection
            </button>
          </div>
        </>
      )}
    </div>
    );
  };

  // Render Step 3: Select Playtesters
  const renderStep3 = () => {
    const displayPlaytesters = sortPlaytesters(
      searchQuery.trim().length >= 2
        ? searchResults
        : showAllPlaytesters
        ? allPlaytesters
        : recommendedPlaytesters,
      playtesterSort
    );

    return (
      <div className="assignment-step step-3">
        <h2>Step 3: Select Playtesters</h2>
        <p className="step-description">
          Select playtesters for <strong>{selectedGame?.name}</strong>. Sort by approved sessions or join date.
        </p>
        {renderPlaytesterSort()}

        <div className="cohort-chip-row">
          {COHORT_SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              className="cohort-chip"
              disabled={!(cohorts[section.id] || []).length}
              onClick={() => selectCohort(section.id)}
            >
              {section.title} ({(cohorts[section.id] || []).length})
            </button>
          ))}
        </div>

        {/* Search Bar */}
        <div className="search-bar">
          <input
            type="text"
            placeholder="🔍 Search playtesters by name or email..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="search-input"
          />
          {isSearching && <span className="search-loading">Searching...</span>}
        </div>

        {/* Selected Count */}
        {selectedPlaytesters.length > 0 && (
          <div className="selection-summary">
            <strong>{selectedPlaytesters.length}</strong> playtester(s) selected
            <button className="btn-clear" onClick={() => setSelectedPlaytesters([])}>
              Clear All
            </button>
          </div>
        )}

        {/* Playtesters List */}
        {loading ? (
          <div className="loading-state">Loading playtesters...</div>
        ) : displayPlaytesters.length === 0 ? (
          <div className="empty-state">
            <p>{searchQuery ? "No playtesters found matching your search." : "No playtesters available."}</p>
          </div>
        ) : (
          <div className="playtesters-selection-list">
            {displayPlaytesters.map((playtester) => (
              <div
                key={playtester.playtesterId}
                className={`playtester-card selectable ${isPlaytesterSelected(playtester.playtesterId) ? "selected" : ""}`}
                onClick={() => togglePlaytesterSelection(playtester)}
              >
                <input
                  type="checkbox"
                  checked={isPlaytesterSelected(playtester.playtesterId)}
                  onChange={() => {}}
                  className="playtester-checkbox"
                />
                <div className="playtester-info">
                  <div className="playtester-header">
                    <h4>{playtester.name || playtester.username || "Unknown"}</h4>
                    <span className="rank-badge">{playtester.rank || "Unranked"}</span>
                  </div>
                  <div className="playtester-email">{playtester.email}</div>
                  <div className="playtester-stats">
                    <span>✅ {getApprovedSessionCount(playtester)} approved sessions</span>
                    <span>{formatJoinedDate(playtester)}</span>
                    {playtester.matchScore ? <span>🎯 {playtester.matchScore}% Match</span> : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="step-actions">
          <button className="btn-secondary" onClick={() => setCurrentStep(2)}>
            ← Back
          </button>
          <button
            className="btn-primary"
            onClick={proceedToConfiguration}
            disabled={selectedPlaytesters.length === 0}
          >
            Continue to Configuration →
          </button>
        </div>
      </div>
    );
  };

  // Render Step 4: Configure Assignment
  const renderStep4 = () => (
    <div className="assignment-step step-4">
      <h2>Step 4: Configure Assignment</h2>
      <p className="step-description">
        Review the deadline from the developer and customize the assignment for{" "}
        <strong>{selectedGame?.name}</strong>.
      </p>

      <div className="configuration-form">
        {/* Selected Playtesters Summary */}
        <div className="form-section">
          <h3>Selected Playtesters ({selectedPlaytesters.length})</h3>
          <div className="selected-playtesters-summary">
            {selectedPlaytesters.map((p) => (
              <span key={p.playtesterId} className="selected-tag">
                {p.name || p.username} ({p.rank})
              </span>
            ))}
          </div>
        </div>

        {/* Deadline */}
        <div className="form-section">
          <label htmlFor="deadline">
            Deadline <span className="required">*</span>
          </label>
          <input
            type="datetime-local"
            id="deadline"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            className="form-input"
            required
          />
          <small className="form-hint">
            Prefills the deadline the developer set when creating the game. You can change it if needed.
          </small>
        </div>

        {/* Custom Message */}
        <div className="form-section">
          <label htmlFor="customMessage">Custom Message (Optional)</label>
          <textarea
            id="customMessage"
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Enter a custom message for the playtesters..."
            rows="4"
            className="form-textarea"
          />
          <small className="form-hint">
            This message will be included in the assignment notification.
          </small>
        </div>

        {/* Notification Toggle */}
        <div className="form-section">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={shouldNotify}
              onChange={(e) => setShouldNotify(e.target.checked)}
            />
            <span>Send notification to playtesters</span>
          </label>
          <small className="form-hint">
            If enabled, selected playtesters will receive an email notification about this assignment.
          </small>
        </div>
      </div>

      <div className="step-actions">
        <button className="btn-secondary" onClick={() => setCurrentStep(3)}>
          ← Back
        </button>
        <button
          className="btn-primary btn-submit"
          onClick={handleSubmitAssignment}
          disabled={!deadline}
        >
          Submit Assignment →
        </button>
      </div>
    </div>
  );

  // Render Step 5: Submitting
  const renderStep5 = () => (
    <div className="assignment-step step-5">
      <div className="loading-state">
        <div className="spinner"></div>
        <h2>Submitting Assignment...</h2>
        <p>Please wait while we assign the playtesters to the game.</p>
      </div>
    </div>
  );

  // Render Step 6: Confirmation
  const renderStep6 = () => (
    <div className="assignment-step step-6">
      <div className="confirmation-panel">
        <div className="success-icon">✅</div>
        <h2>Assignment Successful!</h2>
        <p className="success-message">
          Successfully assigned <strong>{assignmentResult?.assigned || assignmentResult?.assignedCount || selectedPlaytesters.length}</strong> playtester(s) to{" "}
          <strong>{selectedGame?.name}</strong>.
        </p>

        {assignmentResult && (
          <div className="assignment-details">
            <h3>Assignment Details:</h3>
            <ul>
              <li>
                <strong>Game:</strong> {selectedGame?.name}
              </li>
              <li>
                <strong>Playtesters Assigned:</strong> {assignmentResult.assigned || assignmentResult.assignedCount}
              </li>
              <li>
                <strong>Deadline:</strong> {new Date(deadline).toLocaleString()}
              </li>
              <li>
                <strong>Notifications Sent:</strong> {shouldNotify ? "Yes" : "No"}
              </li>
            </ul>

            {assignmentResult.assignments && (
              <div className="assigned-list">
                <h4>Assigned Playtesters:</h4>
                {assignmentResult.assignments.map((assignment) => (
                  <div key={assignment.assignmentId || assignment._id} className="assigned-item">
                    ✓ {assignment.playtesterName || "Playtester"} ({assignment.playtesterEmail})
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="step-actions">
          <button className="btn-primary" onClick={handleReset}>
            Assign More Playtesters
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="manual-assignment">
      <div className="assignment-header">
        <h1>Manual Playtester Assignment</h1>
        {currentStep > 1 && currentStep < 6 && (
          <button className="btn-reset" onClick={handleReset}>
            Start Over
          </button>
        )}
      </div>

      {renderProgressIndicator()}

      {error && (
        <div className="error-banner">
          <strong>⚠️ Error:</strong> {error}
          <button className="close-error" onClick={() => setError(null)}>
            ✕
          </button>
        </div>
      )}

      <div className="assignment-content">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
        {currentStep === 5 && renderStep5()}
        {currentStep === 6 && renderStep6()}
      </div>
    </div>
  );
};

export default ManualAssignment;

